import { type ReactNode } from "react";
import type { SectionNode } from "@workspace/theme-engine";
import { StoreNavbar } from "@/components/StoreNavbar";
import { StoreFooter } from "@/components/StoreFooter";
import { TopBar } from "@/components/TopBar";
import { PromoStrip } from "@/components/PromoStrip";
import { TemplateSlot } from "./TemplateSlot";

/**
 * Maps each allowlisted component id (see `KNOWN_COMPONENTS` in
 * `@workspace/theme-engine`) to a vetted, first-party React renderer. A theme
 * can ONLY compose these ids — there is no mechanism to inject a custom
 * component or author JavaScript. This is the backbone of the "no author JS in
 * the storefront origin" guarantee.
 *
 * `pageContent` is the routed page the storefront would render anyway. Layout
 * components tagged as "full platform content" (product.detail, listing.grid,
 * cart.view, page.content, home.sections) delegate to it, so heavy pages keep
 * their existing vetted implementation while the theme controls composition.
 */
export interface RenderCtx {
  node: SectionNode;
  children: ReactNode;
  pageContent: ReactNode;
  /** `$setting:`-resolved copy of `node.settings`. */
  settings: Record<string, unknown>;
  /** Map of template name -> sandboxed template source from the active theme. */
  templates: Record<string, string>;
  /** Data context passed to sandboxed templates (settings + store info). */
  templateContext: Record<string, unknown>;
}

type Renderer = (ctx: RenderCtx) => ReactNode;

function settingsStyle(p: Record<string, unknown>): React.CSSProperties | undefined {
  const style: React.CSSProperties = {};
  if (typeof p.padding === "string") style.padding = p.padding;
  if (typeof p.background === "string") style.background = p.background;
  if (typeof p.maxWidth === "string") style.maxWidth = p.maxWidth;
  if (typeof p.align === "string") style.textAlign = p.align as React.CSSProperties["textAlign"];
  return Object.keys(style).length ? style : undefined;
}

const passthrough = ({ pageContent }: RenderCtx): ReactNode => <>{pageContent}</>;

export const COMPONENT_RENDERERS: Record<string, Renderer> = {
  // Structural containers
  container: ({ settings, children }) => (
    <div className={typeof settings.className === "string" ? settings.className : "nx-container"} style={settingsStyle(settings)}>
      {children}
    </div>
  ),
  richtext: ({ settings, templateContext }) => (
    <TemplateSlot source={typeof settings.html === "string" ? settings.html : ""} context={templateContext} />
  ),
  spacer: ({ settings }) => (
    <div style={{ height: typeof settings.height === "string" ? settings.height : "1rem" }} aria-hidden />
  ),
  // Resolve the template *name* to its source from the active theme package,
  // then render it through the sandboxed interpreter + DOMPurify.
  "template.slot": ({ node, templates, templateContext }) => (
    <TemplateSlot
      source={node.template ? templates[node.template] ?? "" : ""}
      context={templateContext}
    />
  ),

  // Header region — passthrough to vetted chrome
  "header.bar": () => <StoreNavbar />,
  "header.topbar": () => <TopBar />,
  "header.promostrip": () => <PromoStrip position="top" />,
  "header.nav": () => <StoreNavbar />,
  "header.search": ({ children }) => <>{children}</>,
  "header.logo": ({ children }) => <>{children}</>,
  "header.actions": ({ children }) => <>{children}</>,

  // Footer region
  "footer.columns": () => <StoreFooter />,
  "footer.newsletter": ({ children }) => <>{children}</>,
  "footer.social": ({ children }) => <>{children}</>,
  "footer.payment": ({ children }) => <>{children}</>,
  "footer.copyright": ({ children }) => <>{children}</>,

  // Home / Product / Listing / Cart / Page — full content delegates to the
  // routed page; finer-grained section authoring is a downstream task.
  "home.hero": passthrough,
  "home.featuredProducts": passthrough,
  "home.categoryWall": passthrough,
  "home.banner": passthrough,
  "home.sections": passthrough,
  "product.gallery": passthrough,
  "product.summary": passthrough,
  "product.detail": passthrough,
  "product.tabs": passthrough,
  "product.related": passthrough,
  "listing.toolbar": passthrough,
  "listing.filters": passthrough,
  "listing.grid": passthrough,
  "cart.items": passthrough,
  "cart.summary": passthrough,
  "cart.view": passthrough,
  "page.content": passthrough,
};

export function getRenderer(component: string): Renderer | null {
  return COMPONENT_RENDERERS[component] ?? null;
}
